// TimeTrackingApp.js with Supabase integration and Client/Project Management
import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, Pause, Square, Clock, Calendar, Users, BarChart3, Settings, Plus, Search, Filter, 
  Download, Edit2, Trash2, Timer, FileText, UserCheck, Building2, FolderOpen,
  ChevronDown, Eye, Check, CreditCard, DollarSign, X, CheckCircle, AlertCircle, 
  Briefcase, Switch, LogOut, Minimize2, Shield, Mail, Copy, AlertTriangle, Phone, MapPin, MoreHorizontal, ChevronRight, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { useActiveTimer } from '../hooks/useActiveTimer';
import { supabase } from '../lib/supabase';
import EnhancedTimerView from './EnhancedTimerView';
import TeamManagementView from './TeamManagementView';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import EntriesView from './EntriesView';
import ReportsView from './ReportsView'
import NotificationBell from './NotificationBell';
import FloatingNotificationBell from './FloatingNotificationBell';
import ClientsProjectsView from './clients/ClientsProjectsView';  
import SaaSGovernanceView from './SaaSGovernanceView';
import ReviewEntriesBeta from './ReviewEntriesBeta';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const BillingSettings = () => {
  const { currentTenant } = useTenant();
  const [subscriptionStatus, setSubscriptionStatus] = useState('inactive');
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (currentTenant?.subscription_status) {
      setSubscriptionStatus(currentTenant.subscription_status);
    }
  }, [currentTenant]);

  const handleBillingSuccess = (billingData) => {
    setSubscriptionStatus('active');
    setShowSetup(false);
    // Refresh the page to show updated billing info
    window.location.reload();
  };

  if (subscriptionStatus === 'inactive' || showSetup) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Set Up Per-Seat Billing</h3>
          <p className="text-slate-600">
            Pay only for active team members at $6.99 per seat per month
          </p>
        </div>
        
        <Elements stripe={stripePromise}>
          <PerSeatBillingSetup 
            tenantId={currentTenant.id}
            onSuccess={handleBillingSuccess}
          />
        </Elements>
      </div>
    );
  }

  return <BillingManagement tenantId={currentTenant.id} />;
};
// Per-seat billing setup component
const PerSeatBillingSetup = ({ tenantId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [seatCount, setSeatCount] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);

  const PRICE_PER_SEAT = 6.99;

  useEffect(() => {
    loadSeatCount();
  }, [tenantId]);

  const loadSeatCount = async () => {
    try {
      const { data, error } = await supabase
        .from('tenant_memberships')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('status', 'active');

      if (error) throw error;

      const count = data.length || 1;
      setSeatCount(count);
      setMonthlyTotal(count * PRICE_PER_SEAT);
    } catch (err) {
      console.error('Error loading seat count:', err);
    }
  };

  const handleSubmit = async (event) => {
  event.preventDefault();
  
  if (!stripe || !elements) return;

  setLoading(true);
  setError(null);

  try {
    const cardElement = elements.getElement(CardElement);

      // Create payment method
    const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: currentTenant.name,
        email: user.email,
      },
    });

    if (pmError) {
      setError(pmError.message);
      return;
    }

      // Setup billing via Edge Function
       const { data, error } = await supabase.functions.invoke('stripe-billing', {
      body: {
        action: 'setup_billing',
        data: {
          tenant_id: tenantId,
          user_id: user.id,
          email: user.email,
          tenant_name: currentTenant.name,
          payment_method_id: paymentMethod.id
        }
      }
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(error.message || 'Failed to setup billing');
    }

    console.log('Billing response:', data);

    if (data && data.success) {
      // Update tenant state
      await supabase
        .from('tenants')
        .update({ 
          subscription_status: 'active',
          plan: 'per_seat'
        })
        .eq('id', tenantId);

      onSuccess(data);
    } else {
      throw new Error(data?.error || 'Billing setup failed');
    }

  } catch (err) {
    console.error('Billing setup error:', err);
    setError(err.message || 'An error occurred during billing setup');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-blue-800 mb-4">Per-Seat Pricing</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700">Active seats:</span>
            </div>
            <span className="font-semibold text-blue-800">{seatCount}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700">Price per seat:</span>
            </div>
            <span className="font-semibold text-blue-800">${PRICE_PER_SEAT}/month</span>
          </div>
          
          <div className="border-t border-blue-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 font-medium">Monthly total:</span>
              <span className="font-bold text-blue-900 text-lg">${monthlyTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 border border-slate-300 rounded-lg">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
        
        {error && (
          <div className="text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded">
            {error}
          </div>
        )}
        
        <button
          type="submit"
          disabled={!stripe || loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg disabled:opacity-50 font-medium"
        >
          {loading ? 'Processing...' : `Start Subscription - $${monthlyTotal.toFixed(2)}/month`}
        </button>
        
        <p className="text-xs text-slate-500 text-center">
          Billing adjusts automatically as you add or remove team members
        </p>
      </form>
    </div>
  );
};


// 2. Add Plan Management Component (add this before the BillingManagement component):

const PlanManagement = ({ tenantId, currentPlan, onPlanChange }) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Define your available plans - customize these based on your Stripe products
  const plans = [
    {
      id: 'per_seat',
      name: 'Per-Seat Plan',
      price: 6.99,
      billing: 'per seat/month',
      stripe_price_id: 'price_1Rr4iNLu1sOF2PNdUZVNIZ3t', // Replace with actual price ID
      features: [
        'Unlimited time tracking',
        'Team management',
        'Basic reports',
        'Pay per active seat'
      ],
      description: 'Perfect for growing teams'
    },
    {
      id: 'small_team',
      name: 'Small Team',
      price: 49,
      billing: 'per month',
      stripe_price_id: 'price_1Rr4ihLu1sOF2PNdOA0Azb73', // Replace with actual price ID
      features: [
        'Up to 8 team members',
        'Advanced reports',
        'Priority support',
        'Fixed monthly cost'
      ],
      description: 'Great for small teams'
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 99,
      billing: 'per month',
      stripe_price_id: 'price_1Rr4izLu1sOF2PNd8Jv8n42y', // Replace with actual price ID
      features: [
        'Up to 25 team members',
        'Advanced analytics',
        'Custom integrations',
        'Dedicated support'
      ],
      description: 'For established businesses'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 199,
      billing: 'per month',
      stripe_price_id: 'price_1Rr4jTLu1sOF2PNdG58i9FEY', // Replace with actual price ID
      features: [
        'Unlimited team members',
        'Custom features',
        'SLA guarantee',
        'On-premise option'
      ],
      description: 'For large organizations'
    }
  ];

  const handlePlanChange = async (newPlan) => {
    if (newPlan.id === currentPlan) {
      alert('You are already on this plan.');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'change_plan',
          data: {
            tenant_id: tenantId,
            new_plan_id: newPlan.id,
            new_price_id: newPlan.stripe_price_id
          }
        }
      });

      if (error) throw error;

      if (data && data.success) {
        // Update tenant plan in database
        await supabase
          .from('tenants')
          .update({ 
            plan: newPlan.id,
            monthly_cost: newPlan.price
          })
          .eq('id', tenantId);

        alert(`Successfully switched to ${newPlan.name}! Your next invoice will reflect the change.`);
        setShowPlanModal(false);
        onPlanChange(); // Refresh parent component
      } else {
        throw new Error(data?.error || 'Failed to change plan');
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      alert('Error changing plan: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlanDetails = () => {
    return plans.find(plan => plan.id === currentPlan) || plans[0];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-slate-800">Subscription Plan</h4>
        <button
          onClick={() => setShowPlanModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
        >
          Change Plan
        </button>
      </div>

      {/* Current Plan Display */}
      <div className="border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="font-semibold text-slate-800">{getCurrentPlanDetails().name}</h5>
            <p className="text-sm text-slate-600">{getCurrentPlanDetails().description}</p>
          </div>
          <div className="text-right">
            <div className="font-bold text-slate-900">${getCurrentPlanDetails().price}</div>
            <div className="text-sm text-slate-600">{getCurrentPlanDetails().billing}</div>
          </div>
        </div>
      </div>

      {/* Plan Change Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">Choose Your Plan</h3>
              <button
                onClick={() => setShowPlanModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`border rounded-xl p-6 relative ${
                    plan.id === currentPlan
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {plan.id === currentPlan && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-blue-500 text-white px-2 py-1 rounded-full text-xs">
                        Current
                      </span>
                    </div>
                  )}

                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-slate-800">{plan.name}</h4>
                    <p className="text-sm text-slate-600 mb-2">{plan.description}</p>
                    <div className="text-2xl font-bold text-slate-900">${plan.price}</div>
                    <div className="text-sm text-slate-600">{plan.billing}</div>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanChange(plan)}
                    disabled={loading || plan.id === currentPlan}
                    className={`w-full py-2 px-4 rounded-lg font-medium ${
                      plan.id === currentPlan
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {loading ? 'Processing...' : plan.id === currentPlan ? 'Current Plan' : 'Select Plan'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Billing management component for active subscriptions
const BillingManagement = ({ tenantId }) => {
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const { currentTenant, refreshTenant } = useTenant(); // Make sure you have refreshTenant function

  useEffect(() => {
    loadBillingInfo();
    loadTeamMembers();
  }, [tenantId]);

  const loadBillingInfo = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'get_billing_info',
          data: { tenant_id: tenantId }
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      if (data && data.success) {
        setBillingInfo(data.billing_info);
      } else {
        console.error('No billing info found or error:', data);
      }
    } catch (error) {
      console.error('Error loading billing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('tenant_memberships')
        .select(`
          *,
          user_profiles (email, full_name, avatar_url)
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('created_at');

      if (error) throw error;
      setTeamMembers(data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };


  // The issue is in the BillingManagement component - replace the updateSeatCount function:

const updateSeatCount = async () => {
  try {
    setLoading(true);
    
    // First count the actual active members
    const { data: memberData, error: memberError } = await supabase
      .from('tenant_memberships')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('status', 'active');

    if (memberError) throw memberError;

    const currentSeatCount = memberData.length || 1;
    console.log('Current active members:', currentSeatCount);

    const { data, error } = await supabase.functions.invoke('stripe-billing', {
      body: {
        action: 'update_seat_count',
        data: { tenant_id: tenantId }
      }
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(error.message || 'Failed to update seat count');
    }

    if (data && data.success) {
      await loadBillingInfo(); // Refresh billing info
      if (data.old_seat_count !== data.new_seat_count) {
        alert(`Seat count updated from ${data.old_seat_count} to ${data.new_seat_count} seats. Monthly cost: $${data.monthly_cost}`);
      } else {
        alert(`Seat count is already correct: ${data.new_seat_count} seats ($${data.monthly_cost}/month)`);
      }
    } else {
      throw new Error(data?.error || 'Failed to update seat count');
    }
  } catch (error) {
    console.error('Error updating seat count:', error);
    alert('Error updating seat count: ' + error.message);
  } finally {
    setLoading(false);
  }
};

const handlePlanChange = async () => {
    await loadBillingInfo();
    if (refreshTenant) {
      await refreshTenant();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading billing information...</div>;
  }

  if (!billingInfo) {
    return <div className="text-center py-8 text-red-600">No billing information found</div>;
  }

   return (
    <div className="space-y-6">
      {/* Plan Management Section */}
      <div className="border border-slate-200 rounded-xl p-6">
        <PlanManagement 
          tenantId={tenantId}
          currentPlan={currentTenant?.plan || 'per_seat'}
          onPlanChange={handlePlanChange}
        />
      </div>

      {/* Current Plan Overview - Only show for per-seat plans */}
      {currentTenant?.plan === 'per_seat' && (
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-semibold text-green-800">Per-Seat Plan</h4>
              <p className="text-sm text-green-600">Active subscription</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-800">
                ${billingInfo.monthly_cost}
              </div>
              <div className="text-sm text-green-600">per month</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-green-700 font-medium">Active seats:</span>
              <span className="text-green-600 ml-2">{billingInfo.seat_count}</span>
            </div>
            <div>
              <span className="text-green-700 font-medium">Per seat:</span>
              <span className="text-green-600 ml-2">$6.99/month</span>
            </div>
          </div>
        </div>
      )}

      {/* Team Members - Only show for per-seat plans */}
      {currentTenant?.plan === 'per_seat' && (
        <div className="border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-slate-800">Active Team Members</h4>
            <div className="space-x-2">
              <button
                onClick={updateSeatCount}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                disabled={loading}
              >
                Sync Billing
              </button>
            </div>
          </div>
          
          <div className="space-y-3">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                    <Users className="w-4 h-4 text-slate-600" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">
                      {member.user_profiles?.full_name || member.user_profiles?.email || 'Invited User'}
                    </div>
                    <div className="text-sm text-slate-500 capitalize">{member.role}</div>
                  </div>
                </div>
                <div className="text-sm text-slate-600">
                  {currentTenant?.plan === 'per_seat' ? '$6.99/month' : 'Included'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Details */}
      <div className="border border-slate-200 rounded-xl p-6">
        <h4 className="text-lg font-medium text-slate-800 mb-4">Billing Information</h4>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Next billing date:</span>
            <span className="font-medium">
              {new Date(billingInfo.current_period_end).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Subscription status:</span>
            <span className={`font-medium capitalize ${
              billingInfo.subscription_status === 'active' ? 'text-green-600' : 'text-red-600'
            }`}>
              {billingInfo.subscription_status}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-600">Current plan:</span>
            <span className="font-medium capitalize">
              {currentTenant?.plan?.replace('_', ' ') || 'Per Seat'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

  const TimeTrackingApp = () => {
  const { user, signOut } = useAuth();
  const { tenants, currentTenant, userRole, switchTenant } = useTenant();
  
  // UI State
  const [currentView, setCurrentView] = useState('timer');
  const [showTenantSwitcher, setShowTenantSwitcher] = useState(false);
  
  // Global floating timer state
  const [showGlobalFloatingTimer, setShowGlobalFloatingTimer] = useState(false);
  const [isFloatingMinimized, setIsFloatingMinimized] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  
  // Data State
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [billRates, setBillRates] = useState([]);   
  
  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    clientId: 'all',
    projectId: 'all',
    status: 'all',
    billable: 'all',
    startDate: '',
    endDate: ''
  });
  
  // Custom Hooks
  const { timeEntries, loading: entriesLoading, createTimeEntry, updateTimeEntry, deleteTimeEntry } = useTimeEntries(filters); 
  const { activeTimer, startTimer, stopTimer } = useActiveTimer();
  
  // Helper Functions
  const isAdmin = () => userRole === 'admin';
  const isManager = () => ['admin', 'manager'].includes(userRole);
  
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };
  
  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;  

// Format elapsed time for display
  const formatElapsedTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Global floating timer logic
  useEffect(() => {
    // Show floating timer when there's an active timer and user is NOT on timer page
    if (activeTimer && currentView !== 'timer') {
      setShowGlobalFloatingTimer(true);
    } else {
      setShowGlobalFloatingTimer(false);
    }
  }, [activeTimer, currentView]);

// Sync pause state with activeTimer
  useEffect(() => {
    if (activeTimer) {
      setIsPaused(activeTimer.paused_at !== null);
    }
  }, [activeTimer]);



  // Timer update effect
  useEffect(() => {
    if (activeTimer && !isPaused) {
      const startTime = new Date(activeTimer.started_at || Date.now()).getTime();
      
      const updateTimer = () => {
        setElapsedTime(Date.now() - startTime);
      };

      updateTimer();
      timerRef.current = setInterval(updateTimer, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (!activeTimer) {
        setElapsedTime(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeTimer, isPaused]);

  // Handle floating timer pause/resume
  const handleFloatingPause = async () => {
    try {
      if (isPaused) {
        // Resume timer - calculate the paused duration and add to total
        const pausedDuration = activeTimer.paused_at ? 
          Date.now() - new Date(activeTimer.paused_at).getTime() : 0;
        
        const { error } = await supabase
          .from('active_timers')
          .update({ 
            paused_at: null,
            total_paused_time: (activeTimer.total_paused_time || 0) + pausedDuration
          })
          .eq('id', activeTimer.id);
        
        if (error) throw error;
        setIsPaused(false);
      } else {
        // Pause timer

        const { error } = await supabase
          .from('active_timers')
          .update({ paused_at: new Date().toISOString() })
          .eq('id', activeTimer.id);
        
        if (error) throw error;
        setIsPaused(true);
      }
    } catch (error) {
      console.error('Error toggling timer pause:', error);
      alert('Error updating timer: ' + error.message);
    }
  };

  // Handle floating timer stop
  const handleFloatingStop = async () => {
    if (window.confirm('Are you sure you want to stop the timer?')) {
      try {
        await stopTimer();
        setShowGlobalFloatingTimer(false);
      } catch (error) {
        console.error('Error stopping timer:', error);
        alert('Error stopping timer: ' + error.message);
      }
    }
  };

  // Navigate to timer page
  const goToTimerPage = () => {
    setCurrentView('timer');
    setShowGlobalFloatingTimer(false);
  };



  // Load tenant-specific data
  useEffect(() => {
    if (currentTenant) {
      loadTenantData();
    }
  }, [currentTenant]);

  const loadTenantData = async () => {
    try {
      // Load clients
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')
        .order('name');
      
      if (clientsError) throw clientsError;
      setClients(clientsData || []);

      // Load projects with client information
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          clients (name)
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('status', 'active')
        .order('name');
      
      if (projectsError) throw projectsError;
      setProjects(projectsData || []);

      // Load bill rates (admin only)
      if (isAdmin()) {
        const { data: ratesData, error: ratesError } = await supabase
          .from('bill_rates')
          .select('*')
          .eq('tenant_id', currentTenant.id)
          .order('role');
        
        if (ratesError) throw ratesError;
        setBillRates(ratesData || []);
      }
    } catch (error) {
      console.error('Error loading tenant data:', error);
    }
  }; 
// Global Floating Timer Component
  const GlobalFloatingTimer = () => {
    if (!showGlobalFloatingTimer || !activeTimer) return null;

    const currentProject = projects.find(p => p.id === activeTimer.project_id);
    const currentClient = clients.find(c => c.id === currentProject?.client_id);

    return (
      <div className={`fixed top-4 right-4 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 transition-all duration-300 ${
        isFloatingMinimized ? 'w-16 h-16' : 'w-80'
      }`}>
        {isFloatingMinimized ? (
          // Minimized floating timer
          <button
            onClick={() => setIsFloatingMinimized(false)}
            className="w-16 h-16 flex items-center justify-center text-white bg-gradient-to-r from-green-500 to-green-600 rounded-2xl hover:from-green-600 hover:to-green-700 transition-all shadow-lg"
            title="Expand Timer"
          >
            <Timer className="w-6 h-6" />
          </button>
        ) : (
          // Expanded floating timer
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'
                }`}></div>
                <span className="text-sm font-medium text-slate-700 truncate">
                  {currentProject?.name || 'Project'}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setIsFloatingMinimized(true)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                  title="Minimize"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowGlobalFloatingTimer(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                  title="Hide"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="text-center mb-3 cursor-pointer" onClick={goToTimerPage}>
              <div className={`text-2xl font-mono font-bold transition-colors ${
                isPaused ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {formatElapsedTime(elapsedTime)}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {activeTimer.task_name}
              </div>
              {currentClient && (
                <div className="text-xs text-slate-400 truncate">
                  {currentClient.name}
                </div>
              )}
            </div>
            
            <div className="flex justify-center space-x-2">
              <button
                onClick={handleFloatingPause}
                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                  isPaused
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                }`}
              >
                {isPaused ? 'Resume' : 'Pause'}
              </button>
              <button
                onClick={handleFloatingStop}
                className="px-3 py-1 bg-red-100 text-red-700 hover:bg-red-200 rounded text-xs font-medium transition-all"
              >
                Stop
              </button>
              <button
                onClick={goToTimerPage}
                className="px-3 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded text-xs font-medium transition-all"
              >
                Open
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

// Time Entry Actions State
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editForm, setEditForm] = useState({
    taskName: '',
    description: '',
    durationHours: '',
    isBillable: true,
    entryDate: ''
  });

  const handleUpdateEntryStatus = async (entryId, newStatus) => {
    const updates = { status: newStatus };
    
    if (newStatus === 'submitted') {
      updates.submitted_at = new Date().toISOString();
    } else if (newStatus === 'approved') {
      updates.approved_at = new Date().toISOString();
      updates.approved_by = user.id;
    }

    const { error } = await updateTimeEntry(entryId, updates);
    
    if (error) {
      alert('Error updating entry: ' + error);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const { error } = await deleteTimeEntry(id);
      
      if (error) {
        alert('Error deleting entry: ' + error);
      }
    }
  };

  const handleViewEntry = (entry) => {
    setSelectedEntry(entry);
    setShowViewModal(true);
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setEditForm({
      taskName: entry.task_name || '',
      description: entry.description || '',
      durationHours: entry.duration_hours?.toString() || '',
      isBillable: entry.is_billable || false,
      entryDate: entry.entry_date || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (!editForm.taskName.trim() || !editForm.durationHours) {
      alert('Task name and duration are required');
      return;
    }

    try {
      const updates = {
        task_name: editForm.taskName.trim(),
        description: editForm.description.trim() || null,
        duration_hours: parseFloat(editForm.durationHours),
        is_billable: editForm.isBillable,
        entry_date: editForm.entryDate
      };

      const { error } = await updateTimeEntry(selectedEntry.id, updates);
      
      if (error) {
        throw new Error(error);
      }

      setShowEditModal(false);
      setSelectedEntry(null);
      alert('Time entry updated successfully!');
    } catch (error) {
      console.error('Error updating entry:', error);
      alert('Error updating entry: ' + error.message);
    }
  };

  const handleSubmitEntry = async (entryId) => {
    if (window.confirm('Submit this entry for approval? You won\'t be able to edit it afterwards.')) {
      await handleUpdateEntryStatus(entryId, 'submitted');
    }
  };









  const getProjectsForClient = (clientId) => {
    return projects.filter(p => p.client_id === clientId);
  };

  
  // Navigation Component
  const Navigation = () => (
    <div className="bg-slate-900 text-white h-screen w-64 fixed left-0 top-0 z-10 flex flex-col">
      {/* App Title */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <Timer className="w-6 h-6 text-blue-400" />
          <h1 className="text-xl font-bold">Logworthy</h1>
        </div>
      </div>
	  {/* Tenant Switcher */}
      <div className="p-4 border-b border-slate-700">
        <div className="relative">
          <button
            onClick={() => setShowTenantSwitcher(!showTenantSwitcher)}
            className="w-full flex items-center justify-between p-3 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <div className="font-medium text-sm">{currentTenant?.name}</div>
                <div className="text-xs text-slate-400 capitalize">{userRole}</div>
              </div>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${showTenantSwitcher ? 'rotate-180' : ''}`} />
          </button>
          
          {showTenantSwitcher && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 rounded-lg shadow-xl border border-slate-700 z-20">
              {tenants.map(tenant => (
                <button
                  key={tenant.id}
                  onClick={() => {
                    switchTenant(tenant);
                    setShowTenantSwitcher(false);
                    setCurrentView('timer');
                  }}
                  className={`w-full flex items-center space-x-3 p-3 hover:bg-slate-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                    tenant.id === currentTenant?.id ? 'bg-slate-700' : ''
                  }`}
                >
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded flex items-center justify-center">
                    <Building2 className="w-3 h-3 text-white" />
                  </div>
                  <div className="text-left flex-1">
                    <div className="font-medium text-sm">{tenant.name}</div>
                    <div className="text-xs text-slate-400 capitalize">{tenant.role}</div>
                  </div>
                  {tenant.id === currentTenant?.id && <Check className="w-4 h-4 text-green-400" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      
      
      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2">
        {[
          { id: 'timer', label: 'Time Tracker', icon: Clock, roles: ['admin', 'manager', 'user'] },
          { id: 'entries', label: 'Review Entries', icon: Calendar, roles: ['admin', 'manager', 'user'] },
          { id: 'entries_beta', label: 'Review Entries (Beta)', icon: Sparkles, roles: ['admin', 'manager', 'user'] },
          { id: 'reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'manager', 'user'] },
          { id: 'clients', label: 'Clients & Projects', icon: Briefcase, roles: ['admin', 'manager', 'user'] },
          { id: 'billing', label: 'Billing & Rates', icon: DollarSign, roles: ['admin'] },
          { id: 'team', label: 'Team Management', icon: Users, roles: ['admin'] },
		  { id: 'saas', label: 'SaaS Governance', icon: Shield, roles: ['admin', 'manager'] },
          { id: 'settings', label: 'Settings', icon: Settings, roles: ['admin', 'manager', 'user'] }
        ].filter(item => item.roles.includes(userRole)).map(item => (
          <button
            key={item.id}
            onClick={() => setCurrentView(item.id)}
            className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${
              currentView === item.id 
                ? 'bg-blue-600 text-white' 
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
	  
	   

     {/* User Profile */}
      <div className="p-4 border-t border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.user_metadata?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">
                {user?.user_metadata?.full_name || user?.email}
              </div>
              <div className="text-xs text-slate-400 truncate">{userRole}</div>
            </div>
          </div>
		   {/* Add the notification bell here */}
          <FloatingNotificationBell />
            <button
            onClick={async () => {
              if (window.confirm('Are you sure you want to sign out?')) {
                await signOut();
              }
            }}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
		  
        </div>
      </div>
    </div>
  );

// Timer View Component 
const TimerView = () => (
  <div className="space-y-8">
  
    {/* REMOVED: Organization and Role info */}
    {/* The enhanced timer component handles everything else */}
    <EnhancedTimerView />
  </div>
);


  //ReportsView
  
  //reports end

  


  const BillingRatesView = () => {
  const [activeTab, setActiveTab] = useState('default-rates');
  const [showAddRate, setShowAddRate] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [showProjectRateModal, setShowProjectRateModal] = useState(false);
  const [editingProjectRate, setEditingProjectRate] = useState(null);
  const [projectBillingRates, setProjectBillingRates] = useState([]);
  const [availableBillRateRoles, setAvailableBillRateRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Rate form state
  const [rateForm, setRateForm] = useState({
    role: '',
    hourlyRate: '',
    isDefault: false,
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  // Project rate form state
  const [projectRateForm, setProjectRateForm] = useState({
    projectId: '',
    billingRole: '',
    hourlyRate: '',
    notes: '',
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  // Load available billing roles from database
  useEffect(() => {
    loadAvailableBillingRoles();
    loadProjectBillingRates();
  }, [currentTenant]);

  const loadAvailableBillingRoles = async () => {
    if (!currentTenant?.id) return;
    
    setLoadingRoles(true);
    try {
      const { data, error } = await supabase.rpc('get_tenant_billing_roles', {
        p_tenant_id: currentTenant.id
      });

      if (error) throw error;
      
      // Transform data to match expected format
      const formattedRoles = data.map(role => ({
        value: role.role,
        label: role.label,
        description: role.description,
        suggestedRate: parseFloat(role.hourly_rate) || 0
      }));
      
      setAvailableBillRateRoles(formattedRoles);
    } catch (error) {
      console.error('Error loading billing roles:', error);
      // Fallback to hardcoded roles if database call fails
      setAvailableBillRateRoles([
        { value: 'senior_developer', label: 'Senior Developer', description: 'Experienced developer, complex projects', suggestedRate: 150 },
        { value: 'developer', label: 'Developer', description: 'Regular development work', suggestedRate: 125 },
        { value: 'junior_developer', label: 'Junior Developer', description: 'Entry-level development', suggestedRate: 100 },
        { value: 'designer', label: 'Designer', description: 'UI/UX design work', suggestedRate: 120 },
        { value: 'project_manager', label: 'Project Manager', description: 'Project coordination and management', suggestedRate: 140 },
        { value: 'consultant', label: 'Consultant', description: 'Expert consulting services', suggestedRate: 175 },
        { value: 'qa_tester', label: 'QA Tester', description: 'Quality assurance and testing', suggestedRate: 90 }
      ]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadProjectBillingRates = async () => {
    if (!currentTenant?.id) return;

    try {
      const { data, error } = await supabase
        .from('project_billing_rates_view')
        .select('*')
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('project_name', { ascending: true })
        .order('billing_role', { ascending: true });

      if (error) throw error;
      setProjectBillingRates(data || []);
    } catch (error) {
      console.error('Error loading project billing rates:', error);
      // Fallback to empty array if view doesn't exist yet
      setProjectBillingRates([]);
    }
  };

  const resetRateForm = () => {
    setRateForm({
      role: '',
      hourlyRate: '',
      isDefault: false,
      effectiveDate: new Date().toISOString().split('T')[0]
    });
  };

  const resetProjectRateForm = () => {
    setProjectRateForm({
      projectId: '',
      billingRole: '',
      hourlyRate: '',
      notes: '',
      effectiveDate: new Date().toISOString().split('T')[0]
    });
  };

  // Handle role selection and auto-populate suggested rate
  const handleRoleChange = (newRole) => {
  setRateForm(prev => ({
    ...prev,
    role: newRole
  }));
};

  const handleCreateRate = async (e) => {
    e.preventDefault();
    
    if (!rateForm.role.trim() || !rateForm.hourlyRate) {
      alert('Role and hourly rate are required');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('bill_rates')
        .insert([{
          tenant_id: currentTenant.id,
          role: rateForm.role.trim(),
          hourly_rate: parseFloat(rateForm.hourlyRate),
          is_default: rateForm.isDefault,
          effective_date: rateForm.effectiveDate,
          created_by: user.id
        }])
        .select()
        .single();

      if (error) throw error;

      setBillRates(prev => [...prev, data]);
      resetRateForm();
      setShowAddRate(false);
      
      // Reload available roles to reflect new additions
      loadAvailableBillingRoles();
      
      alert('Bill rate added successfully!');
    } catch (error) {
      console.error('Error creating bill rate:', error);
      alert('Error creating bill rate: ' + error.message);
    }
  };

  const handleUpdateRate = async (e) => {
    e.preventDefault();
    
    if (!rateForm.role.trim() || !rateForm.hourlyRate) {
      alert('Role and hourly rate are required');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('bill_rates')
        .update({
          role: rateForm.role.trim(),
          hourly_rate: parseFloat(rateForm.hourlyRate),
          is_default: rateForm.isDefault,
          effective_date: rateForm.effectiveDate
        })
        .eq('id', editingRate.id)
        .select()
        .single();

      if (error) throw error;

      setBillRates(prev => prev.map(rate => 
        rate.id === editingRate.id ? data : rate
      ));
      
      setEditingRate(null);
      resetRateForm();
      setShowAddRate(false);
      
      // Recalculate existing time entry rates
      await recalculateTimeEntryRates();
      
      alert('Bill rate updated successfully!');
    } catch (error) {
      console.error('Error updating bill rate:', error);
      alert('Error updating bill rate: ' + error.message);
    }
  };

  const startEditingRate = (rate) => {
    setEditingRate(rate);
    setRateForm({
      role: rate.role,
      hourlyRate: rate.hourly_rate.toString(),
      isDefault: rate.is_default,
      effectiveDate: rate.effective_date
    });
    setShowAddRate(true);
  };

  const handleDeleteRate = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this bill rate? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bill_rates')
        .delete()
        .eq('id', rateId);

      if (error) throw error;

      setBillRates(prev => prev.filter(rate => rate.id !== rateId));
      
      // Reload roles after deletion
      loadAvailableBillingRoles();
      
      alert('Bill rate deleted successfully!');
    } catch (error) {
      console.error('Error deleting bill rate:', error);
      alert('Error deleting bill rate: ' + error.message);
    }
  };

  // Handle project-specific rate submission
  const handleProjectRateSubmit = async (e) => {
    e.preventDefault();
    if (!projectRateForm.projectId || !projectRateForm.billingRole || !projectRateForm.hourlyRate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const projectRateData = {
        project_id: projectRateForm.projectId,
        tenant_id: currentTenant.id,
        billing_role: projectRateForm.billingRole,
        hourly_rate: parseFloat(projectRateForm.hourlyRate),
        effective_date: projectRateForm.effectiveDate,
        notes: projectRateForm.notes,
        created_by: user.id
      };

      if (editingProjectRate) {
        const { error } = await supabase
          .from('project_billing_rates')
          .update(projectRateData)
          .eq('id', editingProjectRate.id);
        
        if (error) throw error;
        alert('Project rate updated successfully!');
      } else {
        const { error } = await supabase
          .from('project_billing_rates')
          .insert([projectRateData]);
        
        if (error) throw error;
        alert('Project rate added successfully!');
      }

      loadProjectBillingRates();
      resetProjectRateForm();
      setShowProjectRateModal(false);
      setEditingProjectRate(null);
      
      // Recalculate affected time entry rates
      await recalculateTimeEntryRates();
    } catch (error) {
      console.error('Error saving project rate:', error);
      alert('Error saving project rate: ' + error.message);
    }
  };

  // Initialize project with default rates
  const initializeProjectRates = async (projectId) => {
    if (!window.confirm('This will copy all default tenant rates to this project. Continue?')) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('initialize_project_billing_rates', {
        p_project_id: projectId,
        p_tenant_id: currentTenant.id,
        p_created_by: user.id
      });

      if (error) throw error;
      loadProjectBillingRates();
      
      // Recalculate time entry rates for this project
      await recalculateTimeEntryRates();
      
      alert(`Initialized ${data} billing rates for this project`);
    } catch (error) {
      console.error('Error initializing project rates:', error);
      alert('Error initializing project rates: ' + error.message);
    }
  };

  // Set project default rate for all roles
  const setProjectDefaultForAllRoles = async (projectId, defaultRate) => {
    if (!window.confirm(`Set $${defaultRate}/hour as the rate for ALL billing roles on this project?`)) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('set_project_default_for_all_roles', {
        p_project_id: projectId,
        p_default_rate: parseFloat(defaultRate),
        p_created_by: user.id
      });

      if (error) throw error;
      loadProjectBillingRates();
      
      // Recalculate time entry rates
      await recalculateTimeEntryRates();
      
      alert(`Set default rate for ${data} billing roles`);
    } catch (error) {
      console.error('Error setting project default rates:', error);
      alert('Error setting project default rates: ' + error.message);
    }
  };

  // Delete project rate
  const deleteProjectRate = async (rateId) => {
    if (!window.confirm('Are you sure you want to delete this project-specific rate?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('project_billing_rates')
        .delete()
        .eq('id', rateId);

      if (error) throw error;
      loadProjectBillingRates();
      
      // Recalculate affected time entry rates
      await recalculateTimeEntryRates();
      
      alert('Project rate deleted successfully!');
    } catch (error) {
      console.error('Error deleting project rate:', error);
      alert('Error deleting project rate: ' + error.message);
    }
  };

  // Recalculate time entry rates after rate changes
  const recalculateTimeEntryRates = async () => {
    try {
      const { data, error } = await supabase.rpc('recalculate_time_entry_rates', {
        p_tenant_id: currentTenant.id
      });

      if (error) throw error;
      
      if (data > 0) {
        console.log(`Updated ${data} time entry rates`);
        // Refresh time entries if the parent component provides a refresh function
        if (window.refreshTimeEntries) {
          window.refreshTimeEntries();
        }
      }
    } catch (error) {
      console.error('Error recalculating time entry rates:', error);
    }
  };

  const formatRoleName = (role) => {
    const roleInfo = availableBillRateRoles.find(r => r.value === role);
    return roleInfo ? roleInfo.label : role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleProjectRateUpdate = async (projectId, newRate) => {
    if (newRate < 0) {
      alert('Rate cannot be negative');
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .update({ hourly_rate: parseFloat(newRate) })
        .eq('id', projectId);

      if (error) throw error;

      setProjects(prev => 
        prev.map(project => 
          project.id === projectId 
            ? { ...project, hourly_rate: parseFloat(newRate) }
            : project
        ));

      // Recalculate affected time entry rates
      await recalculateTimeEntryRates();

      alert('Project rate updated successfully!');
    } catch (error) {
      console.error('Error updating project rate:', error);
      alert('Error updating project rate: ' + error.message);
    }
  };

  // Group project billing rates by project
  const groupedProjectRates = projectBillingRates.reduce((acc, rate) => {
    if (!acc[rate.project_id]) {
      acc[rate.project_id] = {
        project: rate,
        rates: []
      };
    }
    acc[rate.project_id].rates.push(rate);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {/* Header with explanation */}
<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
  <h2 className="text-2xl font-bold text-slate-800 mb-2">Billing Rate Management</h2>
  <p className="text-slate-600 mb-4">
    Manage hourly rates for different <strong>billing roles</strong>. These are separate from permission roles and determine how much to charge for different types of work.
  </p>
  
  {/* Tab explanations */}
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
    <div className="bg-white p-4 rounded-lg border border-blue-200">
      <div className="flex items-center mb-2">
        <Settings className="w-5 h-5 text-blue-600 mr-2" />
        <h4 className="font-medium text-blue-800">Default Rates</h4>
      </div>
      <p className="text-slate-600 text-sm">
        Set standard hourly rates by billing role (Senior Developer: $150/hr, Designer: $100/hr). These serve as your baseline rates across all projects.
      </p>
    </div>
    
    <div className="bg-white p-4 rounded-lg border border-blue-200">
      <div className="flex items-center mb-2">
        <Users className="w-5 h-5 text-blue-600 mr-2" />
        <h4 className="font-medium text-blue-800">Project-Specific Rates</h4>
      </div>
      <p className="text-slate-600 text-sm">
        Override default rates for specific project-role combinations. Perfect for premium clients or specialized work that commands different pricing.
      </p>
    </div>
    
    <div className="bg-white p-4 rounded-lg border border-blue-200">
      <div className="flex items-center mb-2">
        <Settings className="w-5 h-5 text-blue-600 mr-2" />
        <h4 className="font-medium text-blue-800">Project Default Rates</h4>
      </div>
      <p className="text-slate-600 text-sm">
        Set a single default rate per project that applies to all time entries when no specific role-based rate is configured.
      </p>
    </div>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
    <div className="bg-white p-4 rounded-lg border border-blue-200">
      <h4 className="font-medium text-blue-800 mb-2">💼 Billing Roles</h4>
      <p className="text-slate-600">
        Used for rate calculations (Senior Developer, Designer, etc.)
      </p>
    </div>
    <div className="bg-white p-4 rounded-lg border border-blue-200">
      <h4 className="font-medium text-blue-800 mb-2">🔐 Permission Roles</h4>
      <p className="text-slate-600">
        Used for app access (Admin, Manager, User)
      </p>
    </div>
  </div>

      
        {/* Tab Navigation */}
        <div className="flex space-x-4 mt-6">
          <button
            onClick={() => setActiveTab('default-rates')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'default-rates'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Default Rates
          </button>
          <button
            onClick={() => setActiveTab('project-rates')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'project-rates'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Project-Specific Rates
          </button>
          <button
            onClick={() => setActiveTab('project-defaults')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'project-defaults'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-blue-50'
            }`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Project Default Rates
          </button>
        </div>
      </div>

      {/* Default Rates Tab */}
      {activeTab === 'default-rates' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-slate-800">Default Bill Rates by Role</h3>
              <p className="text-sm text-slate-600 mt-1">Set standard hourly rates for different billing roles</p>
            </div>
            <button
              onClick={() => setShowAddRate(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Add Rate</span>
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Billing Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Hourly Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Effective Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {billRates.map(rate => (
                  <tr key={rate.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                          {formatRoleName(rate.role).charAt(0)}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-slate-900">{formatRoleName(rate.role)}</div>
                          <div className="text-sm text-slate-500">{rate.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      {formatCurrency(rate.hourly_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rate.is_default ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {rate.is_default ? 'Default' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {new Date(rate.effective_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => startEditingRate(rate)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRate(rate.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {billRates.length === 0 && (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h4 className="font-medium text-slate-800 mb-2">No bill rates configured</h4>
                <p className="text-slate-600 mb-4">Set up hourly rates for different billing roles</p>
                <button
                  onClick={() => setShowAddRate(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Add First Rate
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Project Default Rates Tab */}
      {activeTab === 'project-defaults' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-slate-800">Project Default Rates</h3>
              <p className="text-sm text-slate-600 mt-1">Set a single default rate per project that applies to all time entries when no specific role rate is configured</p>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Default Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Total Billed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {projects.map(project => {
                  const client = clients.find(c => c.id === project.client_id);
                  const projectEntries = timeEntries.filter(entry => 
                    entry.project_id === project.id && entry.is_billable && entry.status === 'approved'
                  );
                  const totalBilled = projectEntries.reduce((sum, entry) => 
                    sum + (entry.duration_hours * entry.hourly_rate), 0
                  );
                  
                  return (
                    <tr key={project.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full mr-3"
                            style={{ backgroundColor: project.color || '#3B82F6' }}
                          ></div>
                          <div className="text-sm font-medium text-slate-900">{project.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {client?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={project.hourly_rate || '0.00'}
                          onChange={(e) => {
                            const newRate = parseFloat(e.target.value) || 0;
                            setProjects(prev => 
                              prev.map(p => 
                                p.id === project.id 
                                  ? { ...p, hourly_rate: newRate }
                                  : p
                              )
                            );
                          }}
                          onBlur={(e) => handleProjectRateUpdate(project.id, e.target.value)}
                          className="w-24 px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(totalBilled)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => setProjectDefaultForAllRoles(project.id, project.hourly_rate || 0)}
                          className="text-blue-600 hover:text-blue-900 transition-colors text-xs"
                          title="Apply this rate to all billing roles for this project"
                        >
                          Apply to All Roles
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Project-Specific Rates Tab */}
      {activeTab === 'project-rates' && (
        <div className="space-y-6">
          {/* Controls */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-800">Project-Specific Billing Rates</h3>
                <p className="text-sm text-slate-600 mt-1">Override default rates for specific projects and billing roles</p>
              </div>
              <button
                onClick={() => setShowProjectRateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Add Project Rate</span>
              </button>
            </div>
          </div>

          {/* Project Rates List */}
          <div className="space-y-6">
            {Object.entries(groupedProjectRates).map(([projectId, projectData]) => {
              const project = projects.find(p => p.id === projectId);
              const client = clients.find(c => c.id === project?.client_id);
              
              return (
                <div key={projectId} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Project Header */}
                  <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: project?.color || '#3B82F6' }}
                        ></div>
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800">{project?.name}</h4>
                          <p className="text-sm text-slate-500">{client?.name}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {projectData.rates.length === 0 && (
                          <button
                            onClick={() => initializeProjectRates(projectId)}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm flex items-center space-x-1"
                          >
                            <Copy className="w-3 h-3" />
                            <span>Initialize Rates</span>
                          </button>
                        )}
                        <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded border">
                          {projectData.rates.length} role{projectData.rates.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Rates Table */}
                  {projectData.rates.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project Rate</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Default Rate</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difference</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {projectData.rates.map(rate => {
                            const defaultRate = billRates.find(br => br.role === rate.billing_role && br.is_default);
                            const difference = defaultRate ? (rate.hourly_rate - defaultRate.hourly_rate) : 0;
                            
                            return (
                              <tr key={rate.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">
                                    {formatRoleName(rate.billing_role)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-semibold text-green-600">
                                    {formatCurrency(rate.hourly_rate)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-500">
                                    {defaultRate ? formatCurrency(defaultRate.hourly_rate) : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`text-sm font-medium ${
                                    difference > 0 ? 'text-green-600' : 
                                    difference < 0 ? 'text-red-600' : 'text-gray-500'
                                  }`}>
                                    {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-xs text-gray-500 truncate max-w-32 block">
                                    {rate.notes || '—'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={() => {
                                      setEditingProjectRate(rate);
                                      setProjectRateForm({
                                        projectId: rate.project_id,
                                        billingRole: rate.billing_role,
                                        hourlyRate: rate.hourly_rate.toString(),
                                        notes: rate.notes || '',
                                        effectiveDate: rate.effective_date
                                      });
                                      setShowProjectRateModal(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteProjectRate(rate.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No project-specific rates configured</p>
                      <p className="text-xs text-gray-400 mt-1">This project will use default tenant rates</p>
                    </div>
                  )}
                </div>
              );
            })}

            {Object.keys(groupedProjectRates).length === 0 && (
              <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Project-Specific Rates</h3>
                <p className="text-gray-500 mb-4">All projects are currently using default tenant rates.</p>
                <button
                  onClick={() => setShowProjectRateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Add First Project Rate
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Default Rate Modal */}
      {showAddRate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingRate ? 'Edit Bill Rate' : 'Add New Bill Rate'}
              </h3>
              <button
                onClick={() => {
                  setShowAddRate(false);
                  setEditingRate(null);
                  resetRateForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
             <div>
  <label className="block text-sm font-medium text-slate-700 mb-2">
    Billing Role *
  </label>
  <input
    type="text"
    value={rateForm.role}
    onChange={(e) => setRateForm(prev => ({ ...prev, role: e.target.value }))}
    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
    placeholder="e.g., Senior Developer, Designer, Project Manager, etc."
    required
  />
  <p className="text-sm text-slate-600 mt-1">
    Enter a custom billing role name. This will be used to identify different types of work and their associated rates.
  </p>
</div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Hourly Rate *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-slate-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rateForm.hourlyRate}
                    onChange={(e) => setRateForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rateForm.isDefault}
                    onChange={(e) => setRateForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mr-3"
                  />
                  <span className="text-sm font-medium text-slate-700">Set as default rate</span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-7">Default rates are used when no project-specific rate is set</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Effective Date
                </label>
                <input
                  type="date"
                  value={rateForm.effectiveDate}
                  onChange={(e) => setRateForm(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddRate(false);
                    setEditingRate(null);
                    resetRateForm();
                  }}
                  className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800 py-2 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingRate ? handleUpdateRate : handleCreateRate}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  {editingRate ? 'Update Rate' : 'Add Rate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Project Rate Modal */}
      {showProjectRateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingProjectRate ? 'Update Project Rate' : 'Add Project Rate'}
              </h3>
              <button
                onClick={() => {
                  setShowProjectRateModal(false);
                  setEditingProjectRate(null);
                  resetProjectRateForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project</label>
                <select
                  value={projectRateForm.projectId}
                  onChange={(e) => setProjectRateForm(prev => ({ ...prev, projectId: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg"
                  disabled={editingProjectRate}
                >
                  <option value="">Select a project...</option>
                  {projects.map(project => {
                    const client = clients.find(c => c.id === project.client_id);
                    return (
                      <option key={project.id} value={project.id}>
                        {project.name} ({client?.name})
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Billing Role</label>
                {loadingRoles ? (
                  <div className="w-full p-2 border border-slate-300 rounded-lg bg-gray-100">
                    Loading roles...
                  </div>
                ) : (
                  <select
                    value={projectRateForm.billingRole}
                    onChange={(e) => setProjectRateForm(prev => ({ ...prev, billingRole: e.target.value }))}
                    className="w-full p-2 border border-slate-300 rounded-lg"
                    disabled={editingProjectRate}
                  >
                    <option value="">Select a role...</option>
                    {availableBillRateRoles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Hourly Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={projectRateForm.hourlyRate}
                  onChange={(e) => setProjectRateForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg"
                />
                {projectRateForm.billingRole && (
                  <p className="text-xs text-slate-500 mt-1">
                    Default rate: {formatCurrency(
                      billRates.find(br => br.role === projectRateForm.billingRole && br.is_default)?.hourly_rate || 0
                    )}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={projectRateForm.notes}
                  onChange={(e) => setProjectRateForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg"
                  rows={2}
                  placeholder="e.g., Special client rate, complex project..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Effective Date</label>
                <input
                  type="date"
                  value={projectRateForm.effectiveDate}
                  onChange={(e) => setProjectRateForm(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  className="w-full p-2 border border-slate-300 rounded-lg"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowProjectRateModal(false);
                  setEditingProjectRate(null);
                  resetProjectRateForm();
                }}
                className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-800 py-2 px-4 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleProjectRateSubmit}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg"
              >
                {editingProjectRate ? 'Update Rate' : 'Add Rate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


   // Settings View Component
  const SettingsView = () => {
    const [activeTab, setActiveTab] = useState('general')
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')
    
    // Usage stats
    const [usageStats, setUsageStats] = useState({
      teamMembers: 0,
      projects: 0,
      monthlyHours: 0
    })
    
    // General Settings
    const [tenantSettings, setTenantSettings] = useState({
      name: currentTenant?.name || '',
      timezone: 'UTC',
      workingHours: {
        start: '09:00',
        end: '17:00',
        workDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
      },
      currency: 'USD',
      dateFormat: 'MM/dd/yyyy',
      timeFormat: '12',
      autoSubmitTimeEntries: false,
      requireProjectSelection: true,
      allowManualTimeEntry: true,
      enableTimeTracking: true
    })

    // Notification Settings
    const [notificationSettings, setNotificationSettings] = useState({
      emailNotifications: true,
      weeklyReports: true,
      timeEntryReminders: true,
      projectDeadlines: true,
      teamUpdates: false,
      invoiceNotifications: true
    })

    // Security Settings
    const [securitySettings, setSecuritySettings] = useState({
      enforceStrongPasswords: true,
      sessionTimeout: 8, // hours
      ipWhitelist: '',
      twoFactorRequired: false,
      passwordExpiryDays: 90
    })

    // Load current settings and usage stats
    useEffect(() => {
      if (currentTenant) {
        loadSettings()
        loadUsageStats()
      }
    }, [currentTenant])

    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('settings')
          .eq('id', currentTenant.id)
          .single()

        if (error) throw error

        if (data.settings) {
          setTenantSettings(prev => ({ ...prev, ...data.settings.general }))
          setNotificationSettings(prev => ({ ...prev, ...data.settings.notifications }))
          setSecuritySettings(prev => ({ ...prev, ...data.settings.security }))
        }
      } catch (error) {
        console.error('Error loading settings:', error)
      }
    }

    const loadUsageStats = async () => {
      try {
        // Load team members count
        const { data: membersData, error: membersError } = await supabase
          .from('tenant_memberships')
          .select('id')
          .eq('tenant_id', currentTenant.id)
          .eq('status', 'active')

        if (membersError) throw membersError

        // Load projects count
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('id')
          .eq('tenant_id', currentTenant.id)

        if (projectsError) throw projectsError

        // Load this month's hours
        const currentMonth = new Date().toISOString().slice(0, 7) // YYYY-MM format
        const { data: hoursData, error: hoursError } = await supabase
          .from('time_entries')
          .select('duration_hours')
          .eq('tenant_id', currentTenant.id)
          .gte('entry_date', currentMonth + '-01')

        if (hoursError) throw hoursError

        const monthlyHours = hoursData?.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0) || 0

        setUsageStats({
          teamMembers: membersData?.length || 0,
          projects: projectsData?.length || 0,
          monthlyHours: Math.round(monthlyHours * 10) / 10 // Round to 1 decimal
        })
      } catch (error) {
        console.error('Error loading usage stats:', error)
      }
    }

    const saveSettings = async (settingsType) => {
      try {
        setLoading(true)
        setMessage('')

        const currentSettings = {
          general: tenantSettings,
          notifications: notificationSettings,
          security: securitySettings
        }

        const { error } = await supabase
          .from('tenants')
          .update({ 
            settings: currentSettings,
            ...(settingsType === 'general' && { name: tenantSettings.name })
          })
          .eq('id', currentTenant.id)

        if (error) throw error

        setMessage(`${settingsType.charAt(0).toUpperCase() + settingsType.slice(1)} settings saved successfully!`)
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000)
      } catch (error) {
        console.error('Error saving settings:', error)
        setMessage('Error saving settings: ' + error.message)
      } finally {
        setLoading(false)
      }
    }

    const tabs = [
      { id: 'general', label: 'General', icon: Settings },
      { id: 'notifications', label: 'Notifications', icon: AlertCircle },
      { id: 'security', label: 'Security', icon: Shield },
      { id: 'billing', label: 'Billing', icon: DollarSign },
      { id: 'integrations', label: 'Integrations', icon: FolderOpen },
      { id: 'export', label: 'Export Data', icon: Download }
    ]

    const GeneralSettings = () => (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Organization Name</label>
          <input
            type="text"
            value={tenantSettings.name}
            onChange={(e) => setTenantSettings(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Timezone</label>
            <select
              value={tenantSettings.timezone}
              onChange={(e) => setTenantSettings(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Asia/Tokyo">Tokyo</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Currency</label>
            <select
              value={tenantSettings.currency}
              onChange={(e) => setTenantSettings(prev => ({ ...prev, currency: e.target.value }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (C$)</option>
              <option value="AUD">AUD (A$)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Working Hours Start</label>
            <input
              type="time"
              value={tenantSettings.workingHours.start}
              onChange={(e) => setTenantSettings(prev => ({ 
                ...prev, 
                workingHours: { ...prev.workingHours, start: e.target.value }
              }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Working Hours End</label>
            <input
              type="time"
              value={tenantSettings.workingHours.end}
              onChange={(e) => setTenantSettings(prev => ({ 
                ...prev, 
                workingHours: { ...prev.workingHours, end: e.target.value }
              }))}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">Time Tracking Settings</label>
          <div className="space-y-3">
            {[
              { key: 'requireProjectSelection', label: 'Require project selection for time tracking' },
              { key: 'allowManualTimeEntry', label: 'Allow manual time entry' },
              { key: 'autoSubmitTimeEntries', label: 'Auto-submit time entries at end of day' },
              { key: 'enableTimeTracking', label: 'Enable time tracking for all users' }
            ].map(setting => (
              <label key={setting.key} className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={tenantSettings[setting.key]}
                  onChange={(e) => setTenantSettings(prev => ({ ...prev, [setting.key]: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">{setting.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => saveSettings('general')}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save General Settings'}
          </button>
        </div>
      </div>
    )

    const NotificationSettings = () => (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-medium text-slate-800 mb-4">Email Notifications</h4>
          <div className="space-y-3">
            {[
              { key: 'emailNotifications', label: 'Enable email notifications', desc: 'Receive important updates via email' },
              { key: 'weeklyReports', label: 'Weekly time reports', desc: 'Get weekly summaries of team time tracking' },
              { key: 'timeEntryReminders', label: 'Time entry reminders', desc: 'Remind users to submit time entries' },
              { key: 'projectDeadlines', label: 'Project deadline alerts', desc: 'Get notified about upcoming project deadlines' },
              { key: 'teamUpdates', label: 'Team activity updates', desc: 'Updates when team members join or leave' },
              { key: 'invoiceNotifications', label: 'Invoice notifications', desc: 'Notifications about invoice generation and payment' }
            ].map(setting => (
              <div key={setting.key} className="flex items-start space-x-3 p-4 border border-slate-200 rounded-lg">
                <input
                  type="checkbox"
                  checked={notificationSettings[setting.key]}
                  onChange={(e) => setNotificationSettings(prev => ({ ...prev, [setting.key]: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">{setting.label}</div>
                  <div className="text-xs text-slate-600">{setting.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => saveSettings('notifications')}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </div>
      </div>
    )

    const SecuritySettings = () => (
      <div className="space-y-6">
        <div>
          <h4 className="text-lg font-medium text-slate-800 mb-4">Password & Authentication</h4>
          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={securitySettings.enforceStrongPasswords}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, enforceStrongPasswords: e.target.checked }))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Enforce strong passwords</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={securitySettings.twoFactorRequired}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, twoFactorRequired: e.target.checked }))}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">Require two-factor authentication</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Session Timeout (hours)</label>
              <input
                type="number"
                min="1"
                max="24"
                value={securitySettings.sessionTimeout}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) }))}
                className="w-32 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Password Expiry (days)</label>
              <input
                type="number"
                min="30"
                max="365"
                value={securitySettings.passwordExpiryDays}
                onChange={(e) => setSecuritySettings(prev => ({ ...prev, passwordExpiryDays: parseInt(e.target.value) }))}
                className="w-32 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-slate-800 mb-4">Access Control</h4>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">IP Whitelist (optional)</label>
            <textarea
              value={securitySettings.ipWhitelist}
              onChange={(e) => setSecuritySettings(prev => ({ ...prev, ipWhitelist: e.target.value }))}
              placeholder="192.168.1.0/24&#10;10.0.0.0/8"
              rows={3}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">Enter IP addresses or CIDR blocks, one per line</p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => saveSettings('security')}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Security Settings'}
          </button>
        </div>
      </div>
    )

    // Update the updateBillingAfterTeamChange function in TeamManagementView.js:

const updateBillingAfterTeamChange = async () => {
  try {
    // Only update if tenant has active subscription
    if (currentTenant?.subscription_status === 'active') {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'update_seat_count',
          data: { tenant_id: currentTenant.id }
        }
      });

      if (error) {
        console.error('Supabase function error:', error);
        return;
      }

      if (data && data.success) {
        console.log('Billing updated:', data);
        // Optionally show a subtle notification to admin
        if (data.old_seat_count !== data.new_seat_count) {
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50';
          notification.textContent = `Billing updated: ${data.new_seat_count} seats ($${data.monthly_cost}/month)`;
          document.body.appendChild(notification);
          
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 5000);
        }
      }
    }
  } catch (error) {
    console.error('Error updating billing:', error);
    // Don't show error to user - billing sync can happen later
  }
};

    const IntegrationsSettings = () => (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { 
              name: 'Slack', 
              desc: 'Get time tracking notifications in Slack', 
              icon: '💬', 
              connected: false,
              color: 'bg-purple-50 border-purple-200'
            },
            { 
              name: 'Google Calendar', 
              desc: 'Sync time entries with calendar events', 
              icon: '📅', 
              connected: true,
              color: 'bg-blue-50 border-blue-200'
            },
            { 
              name: 'QuickBooks', 
              desc: 'Export invoices to QuickBooks', 
              icon: '💰', 
              connected: false,
              color: 'bg-green-50 border-green-200'
            },
            { 
              name: 'Jira', 
              desc: 'Track time against Jira issues', 
              icon: '🎯', 
              connected: false,
              color: 'bg-orange-50 border-orange-200'
            },
            { 
              name: 'GitHub', 
              desc: 'Link commits to time entries', 
              icon: '🐙', 
              connected: true,
              color: 'bg-gray-50 border-gray-200'
            },
            { 
              name: 'Zapier', 
              desc: 'Connect with 2000+ apps', 
              icon: '⚡', 
              connected: false,
              color: 'bg-yellow-50 border-yellow-200'
            }
          ].map(integration => (
            <div key={integration.name} className={`border rounded-xl p-6 ${integration.color}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{integration.icon}</div>
                  <div>
                    <h4 className="font-medium text-slate-800">{integration.name}</h4>
                    <p className="text-sm text-slate-600">{integration.desc}</p>
                  </div>
                </div>
                {integration.connected && (
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                    Connected
                  </span>
                )}
              </div>
              <button className={`w-full py-2 px-4 rounded-lg transition-colors ${
                integration.connected 
                  ? 'bg-red-100 hover:bg-red-200 text-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}>
                {integration.connected ? 'Disconnect' : 'Connect'}
              </button>
            </div>
          ))}
        </div>
      </div>
    )

    const ExportSettings = () => (
      <div className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h4 className="text-lg font-medium text-slate-800 mb-2">Export Your Data</h4>
          <p className="text-slate-600 mb-4">
            Download all your organization's data in various formats for backup or migration purposes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { 
              title: 'Time Entries Export', 
              desc: 'All time entries with detailed information',
              format: 'CSV, Excel, PDF',
              icon: Clock
            },
            { 
              title: 'Team Members Export', 
              desc: 'User profiles and membership information',
              format: 'CSV, Excel',
              icon: Users
            },
            { 
              title: 'Projects & Clients Export', 
              desc: 'All project and client data',
              format: 'CSV, Excel',
              icon: Briefcase
            },
            { 
              title: 'Financial Reports Export', 
              desc: 'Billing rates, invoices, and revenue data',
              format: 'CSV, Excel, PDF',
              icon: DollarSign
            }
          ].map(exportType => (
            <div key={exportType.title} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <exportType.icon className="w-6 h-6 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-slate-800">{exportType.title}</h4>
                  <p className="text-sm text-slate-600 mb-2">{exportType.desc}</p>
                  <p className="text-xs text-slate-500">Formats: {exportType.format}</p>
                </div>
              </div>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          ))}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 mt-1" />
            <div>
              <h4 className="font-medium text-yellow-800 mb-2">Complete Data Export</h4>
              <p className="text-sm text-yellow-700 mb-4">
                Export all your organization's data at once. This includes everything: users, time entries, 
                projects, clients, billing information, and settings.
              </p>
              <button className="bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg transition-colors">
                Request Complete Export
              </button>
              <p className="text-xs text-yellow-600 mt-2">
                Large exports may take a few minutes to prepare. You'll receive an email when ready.
              </p>
            </div>
          </div>
        </div>
      </div>
    )

    if (!isAdmin()) {
      return (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
          <Settings className="w-16 h-16 mx-auto mb-6 text-slate-300" />
          <h3 className="text-2xl font-semibold text-slate-800 mb-4">Access Restricted</h3>
          <p className="text-slate-600">Only administrators can access organization settings.</p>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-xl font-semibold text-slate-800">Organization Settings</h3>
          <p className="text-slate-600 mt-1">Manage your organization's configuration and preferences</p>
          
          {message && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${
              message.includes('Error') 
                ? 'bg-red-50 text-red-700 border border-red-200'
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {message}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'general' && <GeneralSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'billing' && <BillingSettings />}
            {activeTab === 'integrations' && <IntegrationsSettings />}
            {activeTab === 'export' && <ExportSettings />}
          </div>
        </div>
      </div>
    )
  };

  // Don't render if no current tenant selected
  if (!currentTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-800 mb-2">No Tenant Selected</h3>
          <p className="text-slate-600">Please select a tenant to continue.</p>
		  <button
            onClick={async () => {
              if (window.confirm('Are you sure you want to sign out?')) {
                await signOut();
              }
            }}
            className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800"
            title="Sign Out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      {/* Global Floating Timer */}
      <GlobalFloatingTimer />
      <div className="ml-64 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
               <h2 className="text-3xl font-bold text-slate-800">
  {currentView === 'timer' && 'Time Tracker'}
  {currentView === 'entries' && 'Review Entries'}
  {currentView === 'entries_beta' && 'Review Entries (Beta)'}
  {currentView === 'reports' && 'Reports & Analytics'}
  {currentView === 'clients' && 'Clients & Projects'}
  {currentView === 'billing' && 'Billing & Rates'}
  {currentView === 'saas' && 'SaaS Governance'}
  {currentView === 'team' && 'Team Management'}
  {currentView === 'settings' && 'Settings'}
</h2>
               
              </div>
              
              
            </div>
          </div>

          {/* Main Content */}
          {currentView === 'timer' && <TimerView />}
          {currentView === 'entries' && <EntriesView />}
          {currentView === 'entries_beta' && <ReviewEntriesBeta />}
          {currentView === 'reports' && (
  <ReportsView 
    currentTenant={currentTenant}
    timeEntries={timeEntries}
    clients={clients}
    projects={projects}
    formatTime={formatTime}
    formatCurrency={formatCurrency}
  />
)}
          {currentView === 'clients' && isManager() && (
  <ClientsProjectsView
    clients={clients}
    projects={projects}
    timeEntries={timeEntries}
    currentTenant={currentTenant}
    user={user}
    setClients={setClients}
    setProjects={setProjects}
    isManager={isManager}
	formatTime={formatTime}
  />
)}
          {currentView === 'billing' && isAdmin() && <BillingRatesView />}
		  {currentView === 'saas' && isManager() && <SaaSGovernanceView />}
          {currentView === 'team' && isAdmin() && <TeamManagementView />}
          {currentView === 'settings' && <SettingsView />}
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingApp;